import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
    CommunityContext,
    PageContext,
} from '../../../../contexts/ContextCreator';

import Comment from '../Comment/Comment';

import './CommentList.scss';
import {
    emitEngagement,
    objectTypes,
} from '../../../../utilities/getStreamAnalytics';

// renders a list of comments and sub-comments for a given hash
const CommentList = ({
    hash,
    location,
    handleLinkClickMetricEmit,
    handlePostAndJoin,
    submitButtonText,
}) => {
    const { getContent, fetchMoreComments } = useContext(CommunityContext);
    const {
        isLoggedIn,
        wrapAuth,
        pageProps: {
            session: { id, name },
        },
    } = useContext(PageContext);
    const content = getContent(hash);
    const [lastId, setLastId] = useState(content.lastId || null);
    const [expandMore, setExpandMore] = useState(false);
    const [initalLoad, setInitalLoad] = useState(false);
    const isFeed = location === 'masterFeed' || location === 'groupFeed';
    const limit = isFeed ? 5 : null;

    // TODO: if no comments, show "no comments yet" text
    useEffect(() => {
        async function fetchData() {
            if (
                lastId === -1 &&
                typeof content.parentContentHash === 'undefined' &&
                !initalLoad
            ) {
                const lastCommentId = await fetchMoreComments(
                    content.type,
                    hash,
                    null,
                    limit,
                    null,
                    isFeed
                );
                setLastId(lastCommentId);
                setInitalLoad(true);
            } else {
                setInitalLoad(true);
            }
        }
        fetchData();
    }, [lastId, initalLoad]);

    // bail if no content found for provided hash
    if (!content) return null;
    const comments = content.comments.map((c) => (
        <Comment
            key={c.hash}
            commentHash={c.hash}
            nestDepth={0}
            location={location}
            expandMore={expandMore}
            handleLinkClickMetricEmit={handleLinkClickMetricEmit}
            handlePostAndJoin={handlePostAndJoin}
            submitButtonText={submitButtonText}
        />
    ));

    const authMetrics = {
        login_trigger: 'comment',
        community_content_key: `${content.type}-${content.hash}`,
        community_on_comment: content.type === 'community-comment',
    };

    // If there's a last id and there's at least one comment, render the button
    // If there's a zero commentCount, they are all deleted
    const seeMoreCommentsButton =
        lastId !== -1 && !!content.commentCount
            ? wrapAuth(
                  `commentList-${hash}`,
                  <div className="component-Community-CommentBox-buttonContainer">
                      <button
                          onClick={async () => {
                              if (!isLoggedIn()) return;
                              const lastCommentId = await fetchMoreComments(
                                  content.type,
                                  hash,
                                  null,
                                  limit,
                                  lastId,
                                  isFeed
                              );
                              setLastId(lastCommentId);
                              setExpandMore(true);
                              handleLinkClickMetricEmit(
                                  'see-more-comments-click'
                              );
                              emitEngagement(
                                  { id, name },
                                  {
                                      label: 'seeMoreComments',
                                      score: 1,
                                      content: {
                                          foreign_id: `${
                                              objectTypes()[content.type]
                                          }:${content.hash}`,
                                      },
                                  }
                              );
                          }}
                          className="component-Community-CommentBox-SeeMoreCommentsLink"
                          type="button"
                      >
                          See more comments
                      </button>
                  </div>,
                  authMetrics
              )
            : null;

    return (
        <div className="component-Community-CommentBox-CommentList">
            {comments}
            {seeMoreCommentsButton}
        </div>
    );
};

CommentList.propTypes = {
    hash: PropTypes.string.isRequired,
    location: PropTypes.oneOf([
        'masterFeed',
        'miniFeed',
        'groupFeed',
        'singleContent',
        'homePage',
        'single',
    ]).isRequired,
    handleLinkClickMetricEmit: PropTypes.func,
    handlePostAndJoin: PropTypes.func,
    submitButtonText: PropTypes.string,
};

CommentList.defaultProps = {
    handleLinkClickMetricEmit: () => {},
    handlePostAndJoin: null,
    submitButtonText: 'Post',
};

CommentList.displayName = 'CommentList';

export default CommentList;
